import PropTypes from "prop-types";
import { locationShape } from "../shapes";

/**
 * PropTypes for LocationsMonitoring view
 *
 * @property {Array<Location>} [locations] - Array of location data with coordinates
 * @property {Location} [selectedLocation] - Currently selected location
 * @property {function} [onLocationSelect] - Location selection handler
 * @property {boolean} [loading] - Loading state
 */
export const locationsMonitoringPropTypes = {
  locations: PropTypes.arrayOf(locationShape),
  selectedLocation: locationShape,
  onLocationSelect: PropTypes.func,
  loading: PropTypes.bool,
};

/**
 * PropTypes for CreateLocationModal component
 *
 * @property {boolean} isOpen - Modal open state (required)
 * @property {function} onClose - Close handler (required)
 * @property {function} [onSuccess] - Success callback
 * @property {function} addToast - Toast handler (required)
 */
export const createLocationModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  addToast: PropTypes.func.isRequired,
};

/**
 * PropTypes for AssignChargePointsModal component
 *
 * @property {boolean} isOpen - Modal open state (required)
 * @property {function} onClose - Close handler (required)
 * @property {function} [onSuccess] - Success callback
 * @property {function} addToast - Toast handler (required)
 * @property {Array<Object>} locations - Available locations list
 */
export const assignChargePointsModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  addToast: PropTypes.func.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      documentId: PropTypes.string,
      location_id: PropTypes.string,
      name: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      is_unassigned: PropTypes.bool,
    }),
  ),
};

/**
 * PropTypes for MapSection component
 *
 * @property {Array<Location>} locations - Array of locations to display on map (required)
 * @property {Location} [selectedLocation] - Currently selected location
 * @property {function} [onLocationSelect] - Location selection handler
 * @property {function} [onMapLoad] - Callback when Leaflet map instance is ready
 * @property {function} [translate] - Translation helper
 */
export const mapSectionPropTypes = {
  locations: PropTypes.arrayOf(locationShape).isRequired,
  selectedLocation: locationShape,
  onLocationSelect: PropTypes.func,
  onMapLoad: PropTypes.func,
  translate: PropTypes.func,
};

/**
 * PropTypes for internal MapInitializer helper
 */
export const mapInitializerPropTypes = {
  onMapLoad: PropTypes.func,
};

/**
 * PropTypes for AssociateTerminalModal component
 *
 * @property {boolean} isOpen - Modal open state (required)
 * @property {function} onClose - Close handler (required)
 * @property {Object} [location] - Location to associate terminal with
 * @property {function} [onAssociate] - Associate handler callback
 */
export const associateTerminalModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  location: PropTypes.object,
  onAssociate: PropTypes.func,
};

/**
 * PropTypes for SidebarSection component
 *
 * @property {Array<Location>} visibleLocations - Locations visible in current map viewport (required)
 * @property {Location} [selectedLocation] - Currently selected location for details
 * @property {function} onLocationSelect - Callback when location selected (required)
 * @property {function} onClearSelection - Callback to close details view (required)
 * @property {Object} filters - Current filter state (required)
 * @property {function} onFilterChange - Callback for filter updates (required)
 * @property {function} [onRemoteCommandSuccess] - Callback after accepted remote command
 */
export const sidebarSectionPropTypes = {
  visibleLocations: PropTypes.arrayOf(locationShape).isRequired,
  selectedLocation: locationShape,
  onLocationSelect: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    pendingOperator: PropTypes.string,
    pendingSearch: PropTypes.string,
    pendingActiveOnly: PropTypes.bool,
    pendingFaultedOnly: PropTypes.bool,
    pendingNoChargers: PropTypes.bool,
    pendingNoPos: PropTypes.bool,
    appliedOperator: PropTypes.string,
    appliedSearch: PropTypes.string,
    appliedActiveOnly: PropTypes.bool,
    appliedFaultedOnly: PropTypes.bool,
    appliedNoChargers: PropTypes.bool,
    appliedNoPos: PropTypes.bool,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onRemoteCommandSuccess: PropTypes.func,
  onFocusLocation: PropTypes.func.isRequired,
};

/**
 * PropTypes for TableSection component
 *
 * @property {Array<Location>} locations - All locations to display in table (required)
 */
export const tableSectionPropTypes = {
  locations: PropTypes.arrayOf(locationShape).isRequired,
};

/**
 * PropTypes for RemoteCommandsPanel component
 *
 * @property {Object} chargePoint - Charge point object with uid (required)
 * @property {string} chargePoint.uid - Charge point unique identifier (required)
 * @property {function} [onCommandSuccess] - Callback when command succeeds
 */
export const remoteCommandsPanelPropTypes = {
  chargePoint: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
  onCommandSuccess: PropTypes.func,
};

/**
 * PropTypes for ConfigurationPanel component
 *
 * @property {Object} chargePoint - Charge point object with uid (required)
 * @property {string} chargePoint.uid - Charge point unique identifier (required)
 */
export const configurationPanelPropTypes = {
  chargePoint: PropTypes.shape({
    uid: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * PropTypes for TabCarousel component
 *
 * @property {Array<string>} tabLabels - Tab labels displayed in the carousel (required)
 * @property {React.ReactNode} children - TabPanel content (required)
 * @property {string} [className] - Optional className for styling
 * @property {number} [visibleCount] - Number of tabs visible at once
 */
export const tabCarouselPropTypes = {
  tabLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  visibleCount: PropTypes.number,
};
